import { z } from 'zod';
import {
  jobRunOrientationValues,
  jobRunStateValues,
  jobRunSystemValues,
  runDateTypeLabels,
  runDateTypeValues,
} from 'src/apis/transparency-reporting/mappings';
import { getPaginationSchema } from 'src/components/form/zod-schemas';
import {
  preprocessAsArray,
  preprocessAsBoolean,
} from 'src/components/form/zod-utilities';
import { usePageParamsFromSchema } from 'src/hooks/use-page-params-from-schema';
import { useDefaultDateRangeBounds } from 'src/pages/reporting/transparency-reporting/reports/utils';

export function useReportsPageParams() {
  const { start, end, startHour, endHour } = useDefaultDateRangeBounds();

  const schema = getReportsPageParamsSchema(start, end, startHour, endHour);
  return usePageParamsFromSchema(schema, {
    filters: {
      jobName: { label: 'Report Name' },
      state: { label: 'Report State' },
      system: { label: 'System' },
      caller: { label: 'Caller' },
      orientation: { label: 'Type' },
      runFrom: { label: runDateTypeLabels.RUN },
      runTo: { label: runDateTypeLabels.RUN },
      valuesFrom: { label: runDateTypeLabels.VALUE },
      valuesTo: { label: runDateTypeLabels.VALUE },
      runDateType: { label: 'Date Type' },
    },
  });
}

export type ReportsPageParams = ReturnType<typeof useReportsPageParams>;

function getReportsPageParamsSchema(
  start: string,
  end: string,
  startHour: string,
  endHour: string
) {
  return z.object({
    jobName: z.string().nullable().default(null),
    state: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(jobRunStateValues)).default([])
    ),
    system: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(jobRunSystemValues)).default([])
    ),
    caller: z.string().nullable().default(null),
    orientation: z.enum(jobRunOrientationValues).nullable().default(null),
    runFrom: z.string().nullable().default(start),
    runTo: z.string().nullable().default(end),
    valuesFrom: z.string().nullable().default(startHour),
    valuesTo: z.string().nullable().default(endHour),
    runDateType: z.enum(runDateTypeValues).default('RUN'),
    sortColumn: z.string().default('start'),
    sortAscending: z.preprocess(
      preprocessAsBoolean,
      z.boolean().default(false)
    ),
    ...getPaginationSchema().shape,
  });
}
